class OrderTracking {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.apiKey = this.section.dataset.apiKey;
    this.form = this.section.querySelector('#tracking-form');
    this.resultsContainer = this.section.querySelector('#tracking-results');
    this.errorContainer = this.section.querySelector('#tracking-error');
    this.submitBtn = this.section.querySelector('.tracking-submit-btn');

    this.init();
  }

  init() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    const orderNumber = this.section.querySelector('#order-number').value.trim();

    if (!orderNumber) {
      this.showError('Veuillez entrer votre numéro de commande.');
      return;
    }

    this.setLoading(true);
    this.hideError();
    this.hideResults();

    try {
      const trackingData = await this.fetchTrackingInfo(orderNumber);
      this.displayResults(trackingData);
    } catch (error) {
      this.showError(error.message || 'Impossible de récupérer les informations de suivi. Veuillez vérifier votre numéro de commande et réessayer.');
    } finally {
      this.setLoading(false);
    }
  }

  async fetchTrackingInfo(orderNumber) {
    const apiUrl = `https://open.parcelpanel.com/api/v2/tracking/order?order_number=${encodeURIComponent(orderNumber)}`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'x-parcelpanel-api-key': this.apiKey
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Commande introuvable. Veuillez vérifier votre numéro de commande et réessayer.');
      } else if (response.status === 401) {
        throw new Error('Erreur d\'authentification. Veuillez contacter le support.');
      } else if (response.status === 429) {
        throw new Error('Trop de requêtes. Veuillez réessayer dans quelques instants.');
      } else {
        throw new Error('Erreur lors de la récupération des informations.');
      }
    }

    const data = await response.json();

  
    if (data.errors) {
      throw new Error('Commande introuvable. Veuillez vérifier votre numéro de commande.');
    }

    return data.order || data;
  }

  displayResults(trackingData) {
    this.updateSummaryCard(trackingData);

    this.updateTimeline(trackingData);

    this.resultsContainer.style.display = 'block';

    setTimeout(() => {
      this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }

  updateSummaryCard(data) {
    const shipment = data.shipments && data.shipments.length > 0 ? data.shipments[0] : null;

    if (!shipment) {
      this.showError('Aucune information d\'expédition disponible pour cette commande.');
      return;
    }

    const statusText = this.section.querySelector('#tracking-status-text');
    const statusBadge = this.section.querySelector('.tracking-status-badge');
    const status = this.getStatusInfo(shipment.status);

    if (statusText && statusBadge) {
      statusText.textContent = status.text;
      statusBadge.className = `tracking-status-badge tracking-status-badge--${status.type}`;
    }

    const trackingNumberEl = this.section.querySelector('#display-tracking-number');
    if (trackingNumberEl) {
      trackingNumberEl.textContent = shipment.tracking_number || '-';
    }

    const carrierEl = this.section.querySelector('#display-carrier');
    if (carrierEl) {
      carrierEl.textContent = shipment.carrier ? shipment.carrier.name : '-';
    }

    const shipDateEl = this.section.querySelector('#display-ship-date');
    if (shipDateEl) {
      const shipDate = shipment.fulfillment_date || shipment.pickup_date;
      shipDateEl.textContent = shipDate ? this.formatDate(shipDate) : '-';
    }

    const deliveryDateEl = this.section.querySelector('#display-delivery-date');
    if (deliveryDateEl) {
      if (shipment.delivery_date) {
        deliveryDateEl.textContent = this.formatDate(shipment.delivery_date);
      } else if (shipment.estimated_delivery_date) {
        deliveryDateEl.textContent = shipment.estimated_delivery_date.display_text || 'En attente d\'expédition';
      } else {
        deliveryDateEl.textContent = 'En attente d\'expédition';
      }
    }
  }

  updateTimeline(data) {
    const timelineContainer = this.section.querySelector('#tracking-timeline');
    if (!timelineContainer) return;

    timelineContainer.innerHTML = '';

    const shipment = data.shipments && data.shipments.length > 0 ? data.shipments[0] : null;
    const checkpoints = shipment ? (shipment.checkpoints || []) : [];

    if (checkpoints.length === 0) {
      timelineContainer.innerHTML = '<p class="text-center body2">Aucun événement de suivi disponible pour le moment.</p>';
      return;
    }

    checkpoints.forEach((checkpoint, index) => {
      const timelineItem = this.createTimelineItem(checkpoint, index === 0);
      timelineContainer.appendChild(timelineItem);
    });
  }

  createTimelineItem(checkpoint, isLatest = false) {
    const item = document.createElement('div');
    item.className = `tracking-timeline-item${isLatest ? ' tracking-timeline-item--latest' : ''}`;

    const date = checkpoint.checkpoint_time;
    const description = this.translateDescription(checkpoint.detail || '');
    const statusLabel = this.translateStatusLabel(checkpoint.substatus_label || checkpoint.status_label || '');

    item.innerHTML = `
      <div class="tracking-timeline-dot"></div>
      <div class="tracking-timeline-content">
        <div class="tracking-timeline-date body2">
          ${date ? this.formatDateTime(date) : ''}
        </div>
        <div class="tracking-timeline-description">
          ${this.escapeHtml(description)}
        </div>
        ${statusLabel ? `<div class="tracking-timeline-location body2">${this.escapeHtml(statusLabel)}</div>` : ''}
      </div>
    `;

    return item;
  }

  translateDescription(text) {
    const translations = {
      'Your parcel has been delivered in your letter box': 'Votre colis a été livré dans votre boîte aux lettres',
      'Your parcel has been delivered': 'Votre colis a été livré',
      'Your parcel has arrived at its delivery hub. We are preparing its delivery': 'Votre colis est arrivé au centre de distribution. Nous préparons sa livraison',
      'Your parcel has arrived at its delivery hub': 'Votre colis est arrivé au centre de distribution',
      'We are preparing its delivery': 'Nous préparons sa livraison',
      'Your Colissimo is being processed at the local sorting site': 'Votre Colissimo est en cours de traitement au centre de tri local',
      'Your parcel is transiting through our logistics platforms': 'Votre colis transite par nos plateformes logistiques',
      'Your parcel will soon be handed over to us! It is being prepared by the sender': 'Votre colis va bientôt nous être remis ! Il est en cours de préparation par l\'expéditeur',
      'It is being prepared by the sender': 'Il est en cours de préparation par l\'expéditeur',
      'Your parcel is on its way': 'Votre colis est en route',
      'Delivery attempt failed': 'Tentative de livraison échouée',
      'Your parcel is out for delivery': 'Votre colis est en cours de livraison',
      'Package picked up': 'Colis récupéré',
      'Package in transit': 'Colis en transit',
      'Package received': 'Colis reçu',
      'Shipment information received': 'Informations d\'expédition reçues'
    };

    if (translations[text]) {
      return translations[text];
    }

    for (const [english, french] of Object.entries(translations)) {
      if (text.includes(english)) {
        return text.replace(english, french);
      }
    }

    return text;
  }

  translateStatusLabel(text) {
    const translations = {
      'Delivered': 'Livré',
      'Out for delivery': 'En cours de livraison',
      'In transit': 'En transit',
      'Info received': 'Informations reçues',
      'Pending': 'Pas encore expédié',
      'Exception': 'Exception',
      'Failed attempt': 'Tentative échouée'
    };

    return translations[text] || text;
  }

  getStatusInfo(status) {
    const statusMap = {
      'pending': { text: 'Pas encore expédié', type: 'pending' },
      'info_received': { text: 'Informations reçues', type: 'info' },
      'in_transit': { text: 'En transit', type: 'transit' },
      'out_for_delivery': { text: 'En cours de livraison', type: 'delivery' },
      'delivered': { text: 'Livré', type: 'delivered' },
      'failed_attempt': { text: 'Tentative échouée', type: 'failed' },
      'exception': { text: 'Exception', type: 'exception' },
      'expired': { text: 'Expiré', type: 'expired' }
    };

    const normalizedStatus = (status || 'pending').toLowerCase().replace(/\s+/g, '_');
    return statusMap[normalizedStatus] || { text: status || 'Inconnu', type: 'pending' };
  }

  formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  }

  formatDateTime(dateString) {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;

    const submitText = this.submitBtn.querySelector('.tracking-submit-text');
    const loadingSpinner = this.submitBtn.querySelector('.tracking-loading');

    if (isLoading) {
      this.submitBtn.disabled = true;
      if (submitText) submitText.style.display = 'none';
      if (loadingSpinner) loadingSpinner.style.display = 'inline-block';
    } else {
      this.submitBtn.disabled = false;
      if (submitText) submitText.style.display = 'inline';
      if (loadingSpinner) loadingSpinner.style.display = 'none';
    }
  }

  showError(message) {
    if (!this.errorContainer) return;

    const errorText = this.errorContainer.querySelector('p');
    if (errorText) {
      errorText.textContent = message;
    }
    this.errorContainer.style.display = 'block';
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.style.display = 'none';
    }
  }

  hideResults() {
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const trackingSections = document.querySelectorAll('[data-section-name="order-tracking"]');
  trackingSections.forEach(section => {
    new OrderTracking(section);
  });
});
